// IM-UX-163..174 (IM-ADR-019/020): administração da plataforma. Contexto próprio, separado do produto: sem seletor de
// organização, sem busca, sem dados comerciais. Entrar e sair trocam a sessão no servidor e recarregam a página inteira —
// nenhum estado, cache ou seleção do contexto anterior sobrevive. Só aparecem controles que funcionam de verdade.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { PLATFORM_ROLE_LABEL, type PlatformHealthDto } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { Icon, type IconName } from '../components/Icon';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, initials } from '../components/Widgets';
import { platformAdapter } from '../data/platform';
import { useResource } from '../hooks/useResource';
import { friendlyError } from '../lib/errors';
import { NotFound } from '../pages/NotFound';
import { PlatformCommercial } from './PlatformCommercial';
import { PlatformCommunication } from './PlatformCommunication';
import { PlatformDuplicates, PlatformOrganization, PlatformOrganizations } from './PlatformOrganizations';
import { PlatformAdmins, PlatformSessions } from './PlatformPeople';
import { EnrollWithTicket, PlatformMySecurity } from './PlatformSecurity';
import { Kpis, PLATFORM_BASE, SUBSCRIPTION, reloadTo } from './shared';
import '../styles/platform.css';

export { PLATFORM_BASE } from './shared';

export function PlatformRoot() {
  const { me } = useAuth();
  if (me?.context !== 'PLATFORM') return me?.platform ? <PlatformEntry /> : <PlatformDenied />;
  return <PlatformShell />;
}

function PlatformDenied() {
  return (
    <section className="pa-gate" aria-labelledby="page-title">
      <div className="pa-gate-card">
        <BrandLogo variant="public" />
        <EmptyState title="Acesso não autorizado" glyph="lock" tone="amber" action={<Link className="secondary btn-link" to="/">Voltar ao produto</Link>}>
          Esta área é exclusiva da administração da plataforma.
        </EmptyState>
      </div>
    </section>
  );
}

// IM-UX-163/174: entrada explícita com segundo fator (aplicativo ou código de recuperação) ou cadastro inicial.
function PlatformEntry() {
  const { me, logout } = useAuth();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(!!me?.platform?.mfaEnrolled);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!recovery && !/^\d{6}$/.test(code)) { setError('Informe os 6 dígitos do aplicativo autenticador.'); return; }
    if (recovery && code.replace(/[^A-Za-z2-7]/g, '').length !== 10) { setError('Informe o código de recuperação (10 caracteres).'); return; }
    setBusy(true); setError(null);
    try {
      await (recovery ? platformAdapter.enterWithRecovery(code.trim()) : platformAdapter.enter(code));
      reloadTo(PLATFORM_BASE);
    } catch (err) {
      setCode('');
      setError(err instanceof ApiError && err.status === 429 ? 'Muitas tentativas. Aguarde um minuto.' : friendlyError(err, 'Não foi possível entrar agora.'));
      setBusy(false);
    }
  };
  return (
    <section className="pa-gate" aria-labelledby="page-title">
      <div className={`pa-gate-card card${enrolled ? '' : ' pa-gate-wide'}`}>
        <BrandLogo variant="public" />
        <span className="eyebrow">Administração da plataforma</span>
        <h1 id="page-title">{enrolled ? 'Entrar no contexto administrativo' : 'Cadastrar segundo fator'}</h1>
        {enrolled ? (
          <>
            <p className="card-sub">Ao entrar, sua sessão no produto é encerrada. Organizações, dados e seleções do produto não ficam disponíveis aqui.</p>
            <form className="form" onSubmit={submit} noValidate>
              {error && <div className="form-error" role="alert">{error}</div>}
              {recovery ? (
                <div className="field"><label htmlFor="pa-code">Código de recuperação</label>
                  <input id="pa-code" autoComplete="off" spellCheck={false} maxLength={11} value={code} aria-invalid={!!error} placeholder="XXXXX-XXXXX"
                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 11))} /></div>
              ) : (
                <div className="field"><label htmlFor="pa-code">Código do aplicativo autenticador</label>
                  <input id="pa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} aria-invalid={!!error}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} /></div>
              )}
              <button className="primary" type="submit" disabled={busy}>{busy ? 'Verificando…' : 'Entrar na administração'}</button>
            </form>
            <button type="button" className="link-btn pa-switch" onClick={() => { setRecovery((r) => !r); setCode(''); setError(null); }}>
              {recovery ? 'Usar o aplicativo autenticador' : 'Perdi o aparelho: usar código de recuperação'}</button>
          </>
        ) : <EnrollWithTicket onDone={() => setEnrolled(true)} />}
        <div className="pa-gate-foot">
          {me?.platform?.accountKind === 'PLATFORM'
            ? <button type="button" className="link-btn" onClick={logout}>Sair</button>
            : <Link className="link-btn" to="/">Voltar ao produto</Link>}
        </div>
      </div>
    </section>
  );
}

const NAV: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: PLATFORM_BASE, label: 'Visão geral', icon: 'overview', end: true },
  { to: `${PLATFORM_BASE}/organizacoes`, label: 'Organizações', icon: 'accounts' },
  { to: `${PLATFORM_BASE}/administradores`, label: 'Administradores', icon: 'lock' },
  { to: `${PLATFORM_BASE}/sessoes`, label: 'Sessões', icon: 'person' },
  { to: `${PLATFORM_BASE}/estado`, label: 'Estado operacional', icon: 'signals' },
  // IM-UX-190 (SAAS-005): Comercial → Planos e assinaturas.
  { to: `${PLATFORM_BASE}/comercial`, label: 'Comercial', icon: 'sizing' },
  // IM-UX-185 (SAAS-004): Comunicação → E-mail.
  { to: `${PLATFORM_BASE}/comunicacao/email`, label: 'Comunicação', icon: 'inbox' },
  { to: `${PLATFORM_BASE}/seguranca`, label: 'Minha segurança', icon: 'settings' },
];

function PlatformShell() {
  const { me, logout } = useAuth();
  const toast = useToast();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const menuBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => { setDrawer(false); }, [pathname]);
  const role = me?.platform?.role;
  const area = [...NAV].reverse().find((n) => pathname === n.to || pathname.startsWith(`${n.to}/`))?.label ?? 'Administração';
  const exit = async () => {
    setLeaving(true);
    try { await platformAdapter.exit(); reloadTo('/'); } catch (e) { toast(friendlyError(e, 'Não foi possível sair da administração.'), 'error'); setLeaving(false); }
  };
  return (
    <div className="app pa-app">
      <a href="#conteudo" className="skip-link">Pular para o conteúdo</a>
      <aside className={`sidebar ${drawer ? 'open' : ''}`} id="sidebar" aria-label="Navegação da administração">
        <BrandLogo variant="sidebar" />
        <div className="pa-context" role="note"><small>Contexto</small><strong>Administração da plataforma</strong></div>
        <nav className="nav" aria-label="Administração da plataforma">
          <span className="nav-label">Plataforma</span>
          {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end}><Icon name={n.icon} />{n.label}</NavLink>)}
        </nav>
        <div className="pa-exit">
          <button type="button" className="secondary" onClick={exit} disabled={leaving}>{leaving ? 'Saindo…' : 'Sair da administração'}</button>
        </div>
        <div className="side-foot">
          <div className="avatar" aria-hidden="true">{me ? initials(me.user.name) : ''}</div>
          <div className="who"><strong>{me?.user.name}</strong><small>{role ? PLATFORM_ROLE_LABEL[role] : ''}</small></div>
          <button type="button" className="logout" onClick={logout} aria-label="Encerrar sessão" title="Encerrar sessão"><Icon name="logout" /></button>
        </div>
      </aside>
      {drawer && <button type="button" className="scrim" aria-label="Fechar menu" onClick={() => setDrawer(false)} />}
      <main className="main">
        <header className="topbar">
          <button ref={menuBtn} type="button" className="icon-btn mobile-menu" aria-label={drawer ? 'Fechar menu' : 'Abrir menu'} aria-expanded={drawer} aria-controls="sidebar" onClick={() => setDrawer((d) => !d)}><Icon name="menu" /></button>
          <p className="xp-crumb"><span>Plataforma</span><span aria-hidden="true">›</span><strong>{area}</strong></p>
        </header>
        <div className="content" id="conteudo" tabIndex={-1}>
          <Routes>
            <Route index element={<PlatformOverview />} />
            <Route path="organizacoes" element={<PlatformOrganizations />} />
            <Route path="organizacoes/duplicidades" element={<PlatformDuplicates />} />
            <Route path="organizacoes/:id" element={<PlatformOrganization />} />
            <Route path="administradores" element={<PlatformAdmins />} />
            <Route path="sessoes" element={<PlatformSessions />} />
            <Route path="estado" element={<PlatformHealth />} />
            <Route path="comercial/*" element={<PlatformCommercial />} />
            <Route path="comunicacao" element={<Navigate to={`${PLATFORM_BASE}/comunicacao/email`} replace />} />
            <Route path="comunicacao/email" element={<PlatformCommunication />} />
            <Route path="seguranca" element={<PlatformMySecurity />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

const ok = (v: boolean, yes: string, no: string) => <span className={`tag ${v ? 'mint' : 'critical'}`}>{v ? yes : no}</span>;

// IM-UX-164/168: cockpit — indicadores reais e alertas operacionais (sem métricas comerciais inexistentes).
function PlatformOverview() {
  const { state, reload } = useResource((s) => platformAdapter.overview(s), []);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Administração da plataforma" title="Visão geral" subtitle="Organizações, contas e operação. Somente metadados administrativos." actions={<button type="button" className="secondary" onClick={reload}>Atualizar</button>} />
      <AsyncView state={state} onRetry={reload} loading="metrics" empty={null}>
        {(o) => (
          <>
            <dl className="pa-stats" aria-label="Indicadores">
              <div><dt>Organizações</dt><dd>{o.organizations.total}</dd></div>
              <div><dt>Ativas</dt><dd>{o.organizations.active}</dd></div>
              <div className={o.organizations.suspended ? 'warn' : ''}><dt>Suspensas</dt><dd>{o.organizations.suspended}</dd></div>
              <div><dt>Usuários vinculados</dt><dd>{o.users.linked}</dd></div>
              <div><dt>Administradores ativos</dt><dd>{o.platform.admins}</dd></div>
              <div><dt>API</dt><dd>{ok(o.health.api.ok, 'No ar', 'Fora')}</dd></div>
              <div><dt>Workers</dt><dd>{ok(o.health.workers.live > 0, `${o.health.workers.live} ativo(s)`, 'Parados')}</dd></div>
              <div><dt>Fila</dt><dd className="pa-small">{o.health.queue.queued} na fila · {o.health.queue.running} exec. · {o.health.queue.dead} falha(s)</dd></div>
            </dl>
            <article className="card pa-alerts" aria-labelledby="pa-alerts-title">
              <div className="card-head"><div><h2 id="pa-alerts-title">Alertas operacionais</h2></div></div>
              {o.alerts.length === 0 ? <p className="card-sub">Nenhum alerta no momento.</p> : (
                <ul>{o.alerts.map((a) => (
                  <li key={a.code} className={`pa-alert ${a.level}`}><Icon name="alert" /><span>{a.message}</span>{a.to && <Link className="link-btn" to={a.to}>Ver</Link>}</li>))}</ul>
              )}
            </article>
            <div className="pa-grid">
              <article className="card"><div className="card-head"><div><h2>Organizações</h2><div className="card-sub">{o.organizations.createdLast30Days} criadas nos últimos 30 dias</div></div><Link className="link-btn" to={`${PLATFORM_BASE}/organizacoes`}>Ver todas</Link></div>
                <Kpis items={[['Reais', o.organizations.real], ['Técnicas', o.organizations.technical], ['Demonstração', o.organizations.demonstration]]} />
                <p className="cell-sub wide">{o.held.jobs ? `${o.held.jobs} trabalho(s) retido(s) de ${o.held.organizations} organização(ões) suspensa(s)` : 'Nenhum trabalho retido'} · {o.duplicateGroups} grupo(s) de duplicidade provável</p>
              </article>
              <article className="card"><div className="card-head"><div><h2>Contas e acesso</h2><div className="card-sub">{o.sessions.activeTenant} sessões no produto · {o.sessions.activePlatform} na administração</div></div><Link className="link-btn" to={`${PLATFORM_BASE}/administradores`}>Detalhar</Link></div>
                <Kpis items={[['Contas ativas', o.users.active], ['Sem organização', o.users.withoutOrganization], ['Suporte', o.platform.support]]} />
                <p className="cell-sub wide">{o.platform.mfaConfirmed} com segundo fator confirmado{o.platform.pendingEnrollments ? ` · ${o.platform.pendingEnrollments} cadastro(s) pendente(s)` : ''}</p>
              </article>
              <article className="card pa-wide"><div className="card-head"><div><h2>Registro comercial</h2><div className="card-sub">Registro técnico anterior aos planos. Planos, assinaturas, consumo e créditos ficam em Comercial.</div></div><Link className="link-btn" to={`${PLATFORM_BASE}/comercial`}>Comercial</Link></div>
                <ul className="pa-chips">{Object.entries(o.subscriptions).map(([k, n]) => <li key={k}><b>{n}</b> {SUBSCRIPTION[k] ?? k}</li>)}</ul>
              </article>
            </div>
          </>
        )}
      </AsyncView>
    </section>
  );
}

// IM-UX-167: estado operacional.
function PlatformHealth() {
  const { state, reload } = useResource((s) => platformAdapter.health(s), []);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Administração da plataforma" title="Estado operacional" subtitle="API, banco, schema, workers e fila de processamento." actions={<button type="button" className="secondary" onClick={reload}>Atualizar</button>} />
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(h: PlatformHealthDto) => (
          <div className="pa-grid">
            <article className="card"><div className="card-head"><div><h2>Serviços</h2></div></div>
              <dl className="kv">
                <dt>Versão</dt><dd><code>{h.version.slice(0, 12)}</code></dd>
                <dt>API</dt><dd>{ok(h.api.ok, 'No ar', 'Fora')} <span className="cell-sub inline">há {Math.round(h.api.uptimeSeconds / 60)} min</span></dd>
                <dt>Banco de dados</dt><dd>{ok(h.database, 'Disponível', 'Indisponível')}</dd>
                <dt>Schema</dt><dd>{h.schema.ok ? <span className="tag mint">{h.schema.applied}</span> : <span className="tag critical">{h.schema.applied ?? '—'} (esperado {h.schema.expected ?? '—'})</span>}</dd>
                <dt>Workers</dt><dd>{ok(h.workers.live > 0, `${h.workers.live} ativo(s)`, 'Sem sinal')} <span className="cell-sub inline">{h.worker.lastHeartbeatAgeSeconds === null ? 'nunca' : `último sinal há ${Math.round(h.worker.lastHeartbeatAgeSeconds)} s`}</span></dd>
              </dl>
            </article>
            <article className="card"><div className="card-head"><div><h2>Fila</h2></div></div>
              <Kpis items={[['Na fila', h.queue.queued], ['Executando', h.queue.running], ['Falhas', h.queue.dead]]} />
              {h.held.length > 0 && <p className="cell-sub wide">Retidos (organizações suspensas): {h.held.map((x) => `${x.kind} ${x.jobs}`).join(' · ')}</p>}
            </article>
            <article className="card pa-wide"><div className="card-head"><div><h2>Por tipo de trabalho</h2><div className="card-sub">Somente contagens; nenhum conteúdo de tenant</div></div></div>
              {h.queueByKind.length === 0 ? <p className="card-sub">Nenhum trabalho na fila, executando ou com falha.</p> : (
                <div className="table-wrap"><table className="data-table stack" style={{ minWidth: 0 }}>
                  <caption className="sr-only">Fila por tipo de trabalho</caption>
                  <thead><tr><th scope="col">Tipo</th><th scope="col">Na fila</th><th scope="col">Executando</th><th scope="col">Falhas</th></tr></thead>
                  <tbody>{h.queueByKind.map((k) => <tr key={k.kind}><td data-label="Tipo"><code>{k.kind}</code></td><td data-label="Na fila">{k.queued}</td><td data-label="Executando">{k.running}</td><td data-label="Falhas">{k.dead}</td></tr>)}</tbody>
                </table></div>
              )}
            </article>
          </div>
        )}
      </AsyncView>
    </section>
  );
}
